import { call, put, take, select, actionChannel, takeEvery } from 'redux-saga/effects'
import { get } from 'lodash'

import {
  requestRefund,
  requestRefundSucceeded,
  requestRefundFailed,
  SYNC_REFUND_REQUESTED,
  requestTabbedRefundFailed,
  requestTabbedRefundSucceeded,
} from '../actions/refund'

import { makeGetRefund } from '../selectors/refund'
import MutationApi from '../remote/mutationApi'
import {
  clearSelectedOrder,
  replaceItemsFromOrder,
  orderDetailsRequested,
  removeOrder,
  updateOrderTotals,
  syncOrderRequested,
} from '../actions/order'
import { getOrder } from '../selectors/order'

import { REQUEST_TABBED_REFUND, REQUEST_TABBED_REFUND_OFFLINE } from '../actions/refund'

import { APPLICATION_STRINGS } from '../strings'
import { getCartItemsFromOrder, getTaxByItem, getTaxRate } from '../selectors/cart'
import { getMenuServiceCharge } from '../selectors/items'

import { createOrderParams, mapCardReaderDataAsCurrentPayment } from '../utils/orderUtils'
import {
  calculateIndividualTaxAmount,
  calculateServiceCharge,
  calculateServiceFeeTaxRules,
  calculateSubtotal,
  calculateTax,
} from '../utils/totalUtils'
import { formatCartItemModifiers, displayPrice, stringToCents } from '../utils/formatters'
import { ToastManager } from '../utils/toastManager'
import { getManagerId } from '../selectors/attendant'
import { getUseNormalTaxRounding, getUseTaxClasses } from '../selectors/menus'
import { getTaxClasses, getTaxRules } from '../VNTaxes/Selectors'
import { getNetworkAvailableValue } from '../utils/networkConnected'

/**
 * TODO: combine both refundOrder and refundTabbedOrder into one saga
 * Ticket: https://airtable.com/app2mE1mTl0c4GAir/tbl4lm32cBJ1JL2IG/viwLSYXkBjOR5DQyk/recPDxvIjW6zGtirZ?blocks=hide
 */
export function* refundOrder(params = {}) {
  const orderId = get(params, 'payload.orderId', '')
  const lineItemUuids = get(params, 'payload.lineItemIds', [])
  const reason = get(params, 'payload.reason', 'unknown')
  const isCardPresentRefund = get(params, 'payload.isCardPresentRefund', false)
  const cardReaderData = params?.payload?.cardReaderData ?? {}
  const managerId = yield select((state) => getManagerId(state))
  const networkAvailable = getNetworkAvailableValue()

  if (!networkAvailable && isCardPresentRefund) {
    // by this point, card present refunds are already paid
    yield call(refundTabbedOrderOffline, params)
    return
  }

  try {
    if (!networkAvailable) {
      throw new Error('Network unavailable')
    }
    const additionalData = { amountInCents: stringToCents(cardReaderData?.amount) }
    const refundPayment = mapCardReaderDataAsCurrentPayment(cardReaderData, additionalData)
    const payments = isCardPresentRefund ? [refundPayment] : undefined
    const result = yield call(MutationApi.refundOrderItems, orderId, reason, lineItemUuids, false, managerId, payments)
    const orderMutation = result?.data?.orderMutations?.[0]

    if (!orderMutation?.applied) {
      throw new Error(orderMutation.applicationMessage ?? 'Refund not applied')
    }

    yield put(orderDetailsRequested(orderId))
    yield put(requestRefundSucceeded(orderId, result))
    ToastManager.success(APPLICATION_STRINGS.REFUND_ORDER_SUCCEEDED)
  } catch (err) {
    yield put(requestRefundFailed(orderId, { error: err.message ?? err }))
    ToastManager.error(APPLICATION_STRINGS.REFUND_ORDER_FAILED, { autoClose: false })
  }
}

export function* watchRefundOrder() {
  yield takeEvery(requestRefund.type, refundOrder)
}

export function* refundTabbedOrder(params = {}) {
  const orderId = params?.payload?.orderId ?? ''
  const lineItemUuids = params?.payload?.lineItemIds ?? []
  const reason = params?.payload?.reason ?? 'unknown'
  const isCardPresentRefund = params?.payload?.isCardPresentRefund ?? false
  const cardReaderData = params?.payload?.cardReaderData ?? {}
  const managerId = yield select((state) => getManagerId(state))

  try {
    const additionalData = { amountInCents: stringToCents(cardReaderData?.amount) }
    const refundPayment = mapCardReaderDataAsCurrentPayment(cardReaderData, additionalData)
    const payments = isCardPresentRefund ? [refundPayment] : undefined
    const result = yield call(MutationApi.refundOrderItems, orderId, reason, lineItemUuids, false, managerId, payments)
    const orderMutation = result?.data?.orderMutations?.[0]

    if (!orderMutation?.applied) {
      yield put(requestTabbedRefundFailed(orderId, result))
      ToastManager.error(APPLICATION_STRINGS.REFUND_ORDER_FAILED, { autoClose: false })
      return
    }

    yield put(orderDetailsRequested(orderId))
    yield put(requestTabbedRefundSucceeded(orderId, result))

    if (!orderMutation?.lineItems?.length) {
      ToastManager.success(APPLICATION_STRINGS.REFUND_ORDER_CANCELLED)
      return
    }

    ToastManager.success(APPLICATION_STRINGS.REFUND_ORDER_SUCCEEDED)
  } catch (err) {
    ToastManager.error(APPLICATION_STRINGS.REFUND_ORDER_FAILED, { autoClose: false })
    yield put(requestTabbedRefundFailed(orderId, err))
  }
}

export function* refundTabbedOrderOffline(params = {}) {
  const orderId = params.payload.orderId
  const isCardPresentRefund = params.payload.isCardPresentRefund ?? false
  const isCharitySelected = params.payload.isCharitySelected ?? false
  const currentOrder = yield select((state) => getOrder(state, orderId))
  const useNormalTaxRounding = yield select(getUseNormalTaxRounding)
  const taxClasses = yield select(getTaxClasses)
  const taxRules = yield select(getTaxRules)
  const useTaxClasses = yield select(getUseTaxClasses)

  const orderMenuItems = [...createOrderParams(currentOrder).orderMenuItems]
  const refundItemIndexes = params.payload.itemIndexes ?? []

  let taxRulesBreakdown = {}

  //Filter all items from current order removing the voided items
  const newOrderMenuItems = orderMenuItems.filter((e, index) => !refundItemIndexes.includes(index))

  //all items removed then we just delete the order
  if (!newOrderMenuItems.length) {
    yield put(clearSelectedOrder())
    yield put(removeOrder(orderId))
    ToastManager.success(APPLICATION_STRINGS.REFUND_ORDER_FAILED)
    return
  }

  //there are items remaining in the order
  //we also need to filter itemModels
  const itemModels = [...(currentOrder?.itemModels ?? [])]
  const newItemModels = itemModels.filter((e, index) => !refundItemIndexes.includes(index))

  //filter refunded modifiers
  newItemModels.forEach((item, index) => {
    //filter in itemModel
    item.modifiers = item.modifiers?.filter?.((modifier) => !modifier.refund)

    item.price = displayPrice(item, item.modifiers)
    item.modifiersString = formatCartItemModifiers(item.modifiers, item.notes)

    if (newOrderMenuItems[index]?.modifiers?.[0] === undefined) return
    //filter in createOrderParams
    newOrderMenuItems[index].modifiers[0] = newOrderMenuItems[index].modifiers[0].filter(
      (paramsModifier) => !!item.modifiers.find((modelModifier) => modelModifier.lineItemUuid === paramsModifier.uuid),
    )
  })

  //Update items in order
  yield put(replaceItemsFromOrder({ orderId, newOrderMenuItems, newItemModels, needsSync: isCardPresentRefund }))

  //once the items orders are updated we need to calculate totals for the order

  //select all items from the updated order formatted to calculate offline totals
  const updatedCartItems = yield select((state) => getCartItemsFromOrder(state, orderId))

  const taxRate = yield select(getTaxRate)
  const taxByItem = yield select(getTaxByItem)
  const serviceCharge = yield select((state) => getMenuServiceCharge(state))

  const subtotalAmountInCents = calculateSubtotal(updatedCartItems)
  const serviceFee = {
    name: serviceCharge.name,
    total: calculateServiceCharge(serviceCharge, subtotalAmountInCents) ?? 0,
    legal: serviceCharge.legal,
    taxClassUuid: serviceCharge?.taxClassUuid,
  }

  const calculateTaxResult = calculateTax(
    updatedCartItems,
    taxRate,
    taxByItem,
    useNormalTaxRounding,
    useTaxClasses,
    taxClasses,
    taxRules,
    false, //always calculate taxes even if exempted
  )

  taxRulesBreakdown = calculateTaxResult.taxRulesBreakdown || {}

  let serviceFeeTaxAmountInCents = 0

  if (useTaxClasses) {
    const calculateServiceFeeTaxRulesResult = calculateServiceFeeTaxRules(
      taxRulesBreakdown,
      serviceFee,
      useNormalTaxRounding,
      taxClasses,
      taxRules,
      false, //always calculate taxes even if exempted
    )
    serviceFeeTaxAmountInCents = calculateServiceFeeTaxRulesResult.serviceFeeTaxAmountInCents
    taxRulesBreakdown = calculateServiceFeeTaxRulesResult.taxRulesBreakdown || {}
  } else {
    serviceFeeTaxAmountInCents = calculateIndividualTaxAmount(
      taxRate,
      serviceFee.total,
      serviceCharge.taxable,
      useNormalTaxRounding,
    )
  }

  const taxAmountInCents = calculateTaxResult.taxAmountInCents + serviceFeeTaxAmountInCents

  const amountInCents = subtotalAmountInCents + taxAmountInCents + serviceFee.total
  const balanceDueInCents = amountInCents

  if (!currentOrder.taxExempt) {
    yield put(
      updateOrderTotals({
        orderId,
        subtotalAmountInCents,
        taxAmountInCents,
        amountInCents,
        balanceDueInCents,
        serviceFee,
        taxRulesBreakdown,
        charityAmountInCents: isCharitySelected ? 0 : null,
      }),
    )
  } else {
    const exemptedAmountInCents = subtotalAmountInCents + serviceFee.total
    yield put(
      updateOrderTotals({
        orderId,
        subtotalAmountInCents,
        taxAmountInCents,
        amountInCents: exemptedAmountInCents,
        balanceDueInCents: exemptedAmountInCents,
        serviceFee,
        taxRulesBreakdown: {},
        charityAmountInCents: isCharitySelected ? 0 : null,
        exemptableTaxRules: taxRulesBreakdown,
      }),
    )
  }

  if (!isCardPresentRefund) {
    yield put(syncOrderRequested({ orderUuid: orderId }))
  }
  ToastManager.success(APPLICATION_STRINGS.REFUND_ORDER_SUCCEEDED)
}

export function* watchRefundTabbedOrder() {
  yield takeEvery(REQUEST_TABBED_REFUND, refundTabbedOrder)
}

export function* watchRefundTabbedOrderOffline() {
  yield takeEvery(REQUEST_TABBED_REFUND_OFFLINE, refundTabbedOrderOffline)
}

function* syncRefundOrder(action) {
  const orderId = get(action, 'payload.orderId', '')
  const getRefund = makeGetRefund()
  const refund = yield select(getRefund, orderId)

  yield put(requestRefund(refund))
}

export function* watchSyncRefunds() {
  const queue = yield actionChannel(SYNC_REFUND_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield call(syncRefundOrder, payload)
  }
}
